/**
 * Labstep
 *
 * @module prosemirror/extensions/slash
 * @desc Commands for commands plugin
 */

import { Experiment } from 'labstep-web/models/experiment.model';
import type { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { MENU_COMMANDS_ELEMENTS } from 'labstep-web/prosemirror/components/Menu/Commands/elements';
import commandsPlugin, {
  commandsPluginKey,
} from 'labstep-web/prosemirror/extensions/slash/plugin';
import type { IProseMirrorCommandsElement } from 'labstep-web/prosemirror/extensions/slash/types';
import type { IStateDispatchProps } from 'labstep-web/prosemirror/marks/types';
import { getIsStepSelected } from 'labstep-web/prosemirror/utils/selection';
import { ProsemirrorSlashService } from 'labstep-web/services/prosemirror-slash.service';
import type { EditorState } from 'prosemirror-state';
import { getState as getPremiumFeaturesState } from '../../premium-features/selectors';
import {
  enterSubmenu,
  exitSubmenu,
  SET_ELEMENTS,
  selectNext,
  selectPrevious,
  setIsOpen,
  submitElement,
} from './actions';
import {
  getAvailableElements,
  getIndex,
  getIsInSubmenu,
  getIsOpen,
} from './selectors';

const ELEMENTS_SHOULD_CREATE: IProseMirrorCommandsElement[] = [
  'protocol_table',
  'protocol_step',
  'protocol_value',
  'protocol_value_input',
  'protocol_value_output',
  'metadata_input',
  'metadata_output',
  'metadata_text_input',
  'metadata_text_output',
  'metadata_numeric_input',
  'metadata_numeric_output',
  'metadata_date_input',
  'metadata_date_output',
  'metadata_options_input',
  'metadata_options_output',
  'metadata_file_input',
  'metadata_file_output',
  'jupyter_notebook',
  'molecule',
  'conditions',
  'date',
  'smart_table_protocol_condition',
  'smart_table_protocol_value',
];

export type ICommandsExtensionProps = (
  state: EditorState,
  dispatch: IStateDispatchProps['dispatch'],
) => boolean;

export const handleSubmitElement = (
  state: EditorState,
  dispatch: IStateDispatchProps['dispatch'],
  index: number,
) => {
  const element = getAvailableElements(state)[index];
  const commandElement = MENU_COMMANDS_ELEMENTS.find(
    (e) => e.id === element,
  );

  // Check if element has a submenu - enter submenu
  if (commandElement?.submenu) {
    dispatch?.(
      state.tr.setMeta(
        commandsPlugin,
        enterSubmenu(element, commandElement.submenu),
      ),
    );
    return true;
  }

  // Check if element has an onCreate function - execute it directly
  if (commandElement?.onCreate) {
    commandElement.onCreate(state, dispatch);
    // Close the slash menu after executing the command
    dispatch?.(state.tr.setMeta(commandsPlugin, setIsOpen(false)));
    return true;
  }

  // elements that are created and passed to Redux
  if (ELEMENTS_SHOULD_CREATE.indexOf(element) > -1) {
    const premiumFeatures = getPremiumFeaturesState(state);
    const allowsCreation = commandElement?.premiumFeature
      ? premiumFeatures.indexOf(commandElement.premiumFeature) > -1
      : true;
    if (!allowsCreation) {
      dispatch?.(
        state.tr.setMeta(commandsPlugin, submitElement(element)),
      );
    } else {
      ProsemirrorSlashService.createElement(state, dispatch, element);
    }
  } else {
    dispatch?.(
      state.tr.setMeta(commandsPlugin, submitElement(element)),
    );
  }
  return true;
};

export const handleArrowUp: ICommandsExtensionProps = (
  state,
  dispatch,
) => {
  if (getIsOpen(state)) {
    dispatch?.(state.tr.setMeta(commandsPlugin, selectPrevious()));
    return true;
  }
  return false;
};

export const handleArrowDown: ICommandsExtensionProps = (
  state,
  dispatch,
) => {
  if (getIsOpen(state)) {
    dispatch?.(state.tr.setMeta(commandsPlugin, selectNext()));
    return true;
  }
  return false;
};

export const handleEnter: ICommandsExtensionProps = (
  state,
  dispatch,
) => {
  if (getIsOpen(state)) {
    return handleSubmitElement(state, dispatch, getIndex(state));
  }
  return false;
};

export const handleEscape: ICommandsExtensionProps = (
  state,
  dispatch,
) => {
  if (getIsOpen(state)) {
    // If we're in a submenu, exit the submenu and return to main menu
    if (getIsInSubmenu(state)) {
      dispatch?.(state.tr.setMeta(commandsPlugin, exitSubmenu()));
      return true;
    }

    // Otherwise, close the slash menu entirely
    handleSetIsOpen(state, dispatch, false);
    return true;
  }

  return false;
};

/**
 * Close the commands menu if there are no elements to select
 * @param state State
 * @param dispatch Dispatch
 * @returns False, so that the space key can be used to insert a space
 */
export const handleSpace: ICommandsExtensionProps = (
  state,
  dispatch,
) => {
  if (getIsOpen(state) && getAvailableElements(state).length === 0) {
    handleSetIsOpen(state, dispatch, false);
  }
  return false;
};

export const handleTrigger: ICommandsExtensionProps = (
  state,
  dispatch,
) => {
  if (!getIsOpen(state)) {
    handleSetIsOpen(state, dispatch, true);
  }
  return false;
};

export const handleSetIsOpen = (
  state: EditorState,
  dispatch: IStateDispatchProps['dispatch'],
  isOpen: boolean,
) => {
  dispatch?.(state.tr.setMeta(commandsPlugin, setIsOpen(isOpen)));
};

export const handleSetElements = (
  state: EditorState,
  dispatch: IStateDispatchProps['dispatch'],
  entity: Experiment | Protocol,
  experimentWorkflow?: ExperimentWorkflow,
): void => {
  const stepSelected = getIsStepSelected(state);
  let elements: IProseMirrorCommandsElement[] = [
    'heading',
    'bullet_list',
    'ordered_list',
  ];
  if (entity instanceof Experiment) {
    if (!experimentWorkflow && !stepSelected) {
      elements.push('protocol_step');
    } else if (experimentWorkflow) {
      elements.push('protocol_collection');
      elements.push('date');
    }
    if (!experimentWorkflow) {
      elements.push('protocol_timer');
    }
  } else {
    if (!stepSelected) {
      elements.push('protocol_step');
    }
    elements.push('protocol_timer');
  }
  elements = [
    ...elements,
    'file',
    'image',
    'video',
    'link',
    'html_table',
    'protocol_table',
    'protocol_value_input',
    'protocol_value_output',
    'protocol_device',
    'metadata_input',
    'metadata_output',
    /* 'metadata_numeric_input',
    'metadata_options_input',
    'metadata_text_input',
    'metadata_date_input',
    'metadata_file_input',
    'metadata_numeric_output',
    'metadata_options_output',
    'metadata_file_output',
    'metadata_text_output',
    'metadata_date_output', */
    'code',
    'molecule',
    'jupyter_notebook',
    'smart_table_protocol_condition',
  ];
  if (experimentWorkflow && !experimentWorkflow.is_template) {
    elements.splice(-4, 0, 'experiment_workflow_link');
  }

  dispatch?.(
    state.tr.setMeta(commandsPluginKey, {
      type: SET_ELEMENTS,
      elements,
    }),
  );
};
